import Stream from 'flarum/common/utils/Stream';
import Button from 'flarum/common/components/Button';
import ItemList from 'flarum/common/utils/ItemList';
import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Mithril, { Vnode } from 'mithril';
import LayaLogInModal from './LayaLogInModal';
import { getErrorMessage } from '../../common/utils';

export default class LayaSignUpModal extends Modal {
  phone!: Stream<string>;
  code!: Stream<string>;
  password!: Stream<string>;
  loading: boolean = false;

  oninit(vnode: Vnode) {
    super.oninit(vnode);
    this.phone = Stream('');
    this.code = Stream('');
    this.password = Stream('');
  }

  title() {
    return app.translator.trans('core.forum.sign_up.title');
  }

  className() {
    return 'LayaSignUpModal Modal--small';
  }

  content(): Mithril.Children {
    return [
      m('.Modal-body', [
        m('form', { onsubmit: this.onsubmit.bind(this), autocomplete: 'off' }, [
          m('.Form.Form--centered', [
            ...this.fields().toArray(),
            m('.Form-group', [
              m(Button, {
                className: 'Button Button--primary Button--block',
                type: 'submit',
                loading: this.loading,
              }, app.translator.trans('core.forum.sign_up.submit_button')),
            ]),
          ]),
        ]),
      ]),
      this.footer()
    ]
  }

  fields(): ItemList<Mithril.Vnode> {
    const items = new ItemList<Mithril.Vnode>();

    items.add('phone',
      m('.Form-group', [
        m('input.FormControl', {
          name: 'phone',
          type: 'tel',
          placeholder: app.translator.trans('laya-auth.forum.sign_up.phone_placeholder'),
          oninput: (e: Event) => this.phone((e.target as HTMLInputElement).value),
          value: this.phone(),
          disabled: this.loading,
          autocomplete: 'off',
        }),
      ]), 100
    );

    items.add('code',
      m('.Form-group', [
        m('div', { style: { display: 'flex' } }, [
          m('input.FormControl', {
            name: 'code',
            type: 'text',
            placeholder: app.translator.trans('laya-auth.forum.sign_up.code_placeholder'),
            oninput: (e: Event) => this.code((e.target as HTMLInputElement).value),
            value: this.code(),
            disabled: this.loading,
            style: { flex: 1, marginRight: '10px' },
            autocomplete: 'off',
          }),
          m(Button, {
            className: 'Button',
            onclick: this.sendCode.bind(this),
            loading: this.loading,
          }, app.translator.trans('laya-auth.forum.sign_up.send_code_button')),
        ]),
      ]), 90
    );

    items.add('password',
      m('.Form-group', [
        m('input.FormControl', {
          name: 'password',
          type: 'password',
          placeholder: app.translator.trans('laya-auth.forum.sign_up.password_placeholder'),
          oninput: (e: Event) => this.password((e.target as HTMLInputElement).value),
          value: this.password(),
          disabled: this.loading,
          autocomplete: 'new-password',
        }),
      ]), 80
    );

    return items;
  }

  footer() {
    return m('.Modal-footer', [
      m("p.SignUpModal-logIn", [
        app.translator.trans('core.forum.sign_up.log_in_text', {
          a: m('a', {
            onclick: () => {
              app.modal.show(LayaLogInModal)
            }
          })
        })
      ])
    ])
  }

  sendCode() {
    if (!this.phone()) {
      app.alerts.show({ type: 'error' }, app.translator.trans('laya-auth.forum.sign_up.phone_required_alert'));
      return;
    }
    app.alerts.clear();
    this.loading = true;
    m.redraw();

    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/laya/send-code',
      body: { phone: this.phone(), scene: 'register' },
      errorHandler: (error: any) => {
        this.loading = false;
        m.redraw();
        // 尝试多种方式获取 ret 错误码和 msg
        const ret = error?.response?.ret ?? error?.responseJSON?.ret ?? error?.body?.ret ?? error?.ret;
        const msg = error?.response?.msg ?? error?.responseJSON?.msg ?? error?.body?.msg ?? error?.msg;
        if (ret !== undefined && ret !== null) {
          app.alerts.show({ type: 'error' }, getErrorMessage(ret, msg));
          return;
        }
        // 如果无法获取 ret，使用默认错误处理
        this.onerror(error);
      },
    }).then((response: any) => {
      this.loading = false;
      m.redraw();
      // 检查 API 返回的 ret 错误码
      if (response && response.ret !== undefined && response.ret !== 0) {
        app.alerts.show({ type: 'error' }, getErrorMessage(response.ret, response.msg));
        return;
      }
      app.alerts.show({ type: 'success' }, app.translator.trans('laya-auth.forum.sign_up.code_sent_alert'));
    });
  }

  onsubmit(e: SubmitEvent) {
    e.preventDefault();
    this.loading = true;

    const data = {
      phone: this.phone(),
      code: this.code(),
      password: this.password(),
    };

    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/laya/register',
      body: data,
      errorHandler: (error: any) => {
        this.loading = false;
        m.redraw();
        // 尝试多种方式获取 ret 错误码和 msg
        const ret = error?.response?.ret ?? error?.responseJSON?.ret ?? error?.body?.ret ?? error?.ret;
        const msg = error?.response?.msg ?? error?.responseJSON?.msg ?? error?.body?.msg ?? error?.msg;
        if (ret !== undefined && ret !== null) {
          app.alerts.show({ type: 'error' }, getErrorMessage(ret, msg));
          return;
        }
        this.onerror(error);
      },
    }).then((response: any) => {
      // 检查 API 返回的 ret 错误码
      if (response && response.ret !== undefined && response.ret !== 0) {
        this.loading = false;
        m.redraw();
        app.alerts.show({ type: 'error' }, getErrorMessage(response.ret, response.msg));
        return;
      }
      window.location.reload();
    });
  }
}
